import React, { useMemo, useState, useContext, useEffect } from 'react';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft, SubHeaderRight } from '../../../../layout/SubHeader/SubHeader';
import Page from '../../../../layout/Page/Page';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody } from '../../../../components/bootstrap/Card';
import Dropdown, { DropdownToggle, DropdownMenu, DropdownItem } from '../../../../components/bootstrap/Dropdown';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import SelectStore from '../../../../components/MyCustom/SelectStore';
import Spinner from '../../../../components/bootstrap/Spinner';
import DiscountModal from './DiscountModal';
import Popovers from '../../../../components/bootstrap/Popovers';
import { DefaultContext } from '../../../../contexts/default';
import BillingScheduleDB from '../../../../database/wrappers/billingSchedule';
import BillingChargesDB from '../../../../database/wrappers/billingCharges';
import StoreDB from '../../../../database/wrappers/store';
import BillingValuesDB from '../../../../database/wrappers/billingValues';
import api from '../../../../services/api';
import { where } from 'firebase/firestore';
import Money from '../../../../helpers/utils/money/money';
import showNotification from '../../../../components/extras/showNotification';

const months = [
  'Jan', 'Fev', 'Mar', 'Abr', 'mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const StoreSubscription = () => {
  const { estabSelected, storeSelected } = useContext(DefaultContext);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(months[currentMonthIndex]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const monthIndex = useMemo(() => months.indexOf(selectedMonth), [selectedMonth]);

  const handleRefresh = () => {
    setRefreshKey(oldKey => oldKey + 1);
  };

  useEffect(() => {
    if (!estabSelected) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const storeDB = new StoreDB(estabSelected);
        const stores = await storeDB.getAll();
        const storeMap = stores.reduce((acc, store) => {
          acc[store.id] = store.name;
          return acc;
        }, {});

        const billingDB = new BillingChargesDB();

        let constraints = [where('estabId', '==', estabSelected)];
        if (storeSelected) {
          constraints.push(where('storeId', '==', storeSelected));
        }

        let charges = await billingDB.getAll(...constraints);

        const scheduleDB = new BillingScheduleDB();
        let scheduleConstraints = [where('estabId', '==', estabSelected)];
        if (storeSelected) {
          scheduleConstraints.push(where('storeId', '==', storeSelected));
        }
        const schedules = await scheduleDB.getAll(...scheduleConstraints);

        const scheduleMap = schedules.reduce((acc, schedule) => {
          acc[schedule.storeId] = schedule;
          return acc;
        }, {});

        const yearCharges = charges.filter(charge => {
          if (!charge.created_at) return false;
          return charge.created_at.toDate().getFullYear() === selectedYear;
        });

        const currentMonthCharges = yearCharges.filter(charge => {
          const date = charge.created_at.toDate();
          return date.getMonth() === monthIndex;
        });

        const newRows = currentMonthCharges.map(charge => {
          const status = mapStatus(charge.status);
          const schedule = scheduleMap[charge.storeId];

          let currentChargeDate = '--';
          let nextChargeDate = '-';

          const getFormattedDate = (dayValue, baseDateObj) => {
            if (!dayValue) return '--';

            let day;
            if (dayValue && typeof dayValue === 'object' && typeof dayValue.toDate === 'function') {
              day = dayValue.toDate().getDate();
            } else {
              day = parseInt(dayValue);
            }

            if (!day || isNaN(day)) return '--';

            const d = new Date(baseDateObj);
            const year = d.getFullYear();
            const month = d.getMonth();
            const lastDay = new Date(year, month + 1, 0).getDate();
            const finalDay = Math.min(day, lastDay);

            return new Date(year, month, finalDay).toLocaleDateString('pt-BR');
          };

          if (charge.dueDate) {
            currentChargeDate = charge.dueDate.toDate().toLocaleDateString('pt-BR');
          } else if (schedule) {
            currentChargeDate = getFormattedDate(schedule.currentChargeDate, charge.created_at.toDate());
          }

          return {
            id: charge.id,
            data: `${selectedMonth}. ${selectedYear}`,
            vencimento: currentChargeDate,
            pagamento: charge.status === 'PAID' ? (charge.updated_at ? charge.updated_at.toDate().toLocaleDateString('pt-BR') : '--') : '--',
            assinatura: 1,
            pos: charge.items.pos?.qtdInitial || 0,
            posDelivery: charge.items.posDelivery?.qtdInitial > 0 ? 'Ativo' : 'Inativo',
            totem: charge.items.totem?.qtdInitial || 0,
            fiscal: charge.fiscalQtd,
            locacao: Money.centsToMaskMoney(charge.items.totem?.totalFinal || 0),
            desconto: Money.centsToMaskMoney(charge.totalDiscount),
            totalLiquido: Money.centsToMaskMoney(charge.totalInitial),
            imposto: Money.centsToMaskMoney(0),
            total: Money.centsToMaskMoney(charge.totalToPay),
            status: status,
            original: charge
          };
        });

        setRows(newRows);

      } catch (error) {
        let errorMessage = 'Não foi possível carregar as informações de assinatura. Tente novamente mais tarde.';
        let errorTitle = 'Erro ao buscar dados';

        if (error.code === 'failed-precondition' && error.message.includes('index')) {
          errorMessage = 'O sistema requer a criação de um índice no banco de dados para realizar esta consulta. Verifique o console para o link de criação.';
          errorTitle = 'Configuração necessária';
        }

        showNotification(
          errorTitle,
          errorMessage,
          'danger'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [estabSelected, storeSelected, selectedYear, selectedMonth, monthIndex, refreshKey]);

  const mapStatus = (status) => {
    if (status === 'PENDING') return 'Pendente';
    if (status === 'GENERATED') return 'Pendente';
    if (status === 'PAID') return 'Pago';
    if (status === 'CANCELED') return 'Vencida';
    return status;
  };

  const handleDeleteDiscount = async (row) => {
    setDeletingId(row.id);
    try {
      const payload = {
        billingId: row.id,
        justification: '...',
        items: {
          system: 0,
          pos: 0,
          posDelivery: 0,
          printer: 0,
          totem: 0,
          tablet: 0
        }
      };

      await api.put('billing/discount', payload);

      showNotification(
        'Sucesso',
        'Exclusão dos descontos foi um sucesso',
        'success'
      );
      handleRefresh();
    } catch (error) {
      showNotification('Erro', 'Erro ao excluir descontos', 'danger');
    } finally {
      setDeletingId(null);
    }
  };


  const columns = useMemo(() => [
    { label: 'DATA', field: 'data' },
    { label: 'VENCIMENTO', field: 'vencimento' },
    { label: 'PAGAMENTO', field: 'pagamento' },
    { label: 'ASSINATURA', field: 'assinatura' },
    { label: 'POS', field: 'pos' },
    {
      label: 'POS DELIVERY',
      field: 'posDelivery',
      format: row => (
        <Button
          isLight
          color={row.posDelivery === 'Ativo' ? 'success' : 'danger'}
          icon='Circle'
          className='text-nowrap'
          size="sm"
        >
          {row.posDelivery}
        </Button>
      )
    },
    { label: 'TOTEM', field: 'totem' },
    { label: 'FISCAL', field: 'fiscal' },
    { label: 'LOCAÇÃO', field: 'locacao' },
    { label: 'DESCONTO', field: 'desconto' },
    { label: 'TOTAL LIQUIDO', field: 'totalLiquido' },
    { label: 'TOTAL', field: 'total' },
    {
      label: 'STATUS',
      field: 'status',
      format: row => {
        let color = 'success';
        let icon = 'Savings';

        if (row.status === 'Pendente') {
          color = 'warning';
          icon = 'AccessTime';
        }
        if (row.status === 'Vencida') {
          color = 'danger';
          icon = 'Warning';
        }

        return (
          <Button
            isLight
            color={color}
            icon={icon}
            className='text-nowrap'
            size="sm"
          >
            {row.status}
          </Button>
        )
      }
    },
    {
      label: 'AÇÃO',
      field: 'actions',
      format: row => (
        <div className='d-flex gap-2'>
          <Popovers trigger='hover' desc={'Editar'}>
            <Button
              color="dark"
              icon="Edit"
              size="sm"
              shadow="sm"
              onClick={() => {
                setSelectedCharge(row.original);
                setIsDiscountModalOpen(true);
              }}
            />
          </Popovers>
          <Popovers trigger='hover' desc={'Excluir'}>
            <Button
              color="danger"
              icon={deletingId === row.id ? undefined : "Delete"}
              size="sm"
              shadow="sm"
              onClick={() => handleDeleteDiscount(row.original)}
              disabled={deletingId === row.id}
            >
              {deletingId === row.id && <Spinner isSmall color="light" inButton="onlyIcon" />}
            </Button>
          </Popovers>
        </div>
      ),
    }
  ], [deletingId]);

  const columnsLocacao = useMemo(() => [
    { label: 'DATA', field: 'data' },
    { label: 'POS', field: 'pos' },
    { label: 'TOTEM', field: 'totem' },
    { label: 'DESCONTO', field: 'desconto' },
    { label: 'TOTAL LIQUIDO', field: 'totalLiquido' },
    { label: 'TOTAL', field: 'total' },
    {
      label: 'AÇÃO',
      field: 'actions',
      format: row => (
        <div className='d-flex gap-2'>
          <Popovers trigger='hover' desc={'Editar'}>
            <Button
              color="dark"
              icon="Edit"
              size="sm"
              shadow="sm"
              onClick={() => {
                setSelectedCharge(row.original);
                setIsDiscountModalOpen(true);
              }}
            />
          </Popovers>
          <Popovers trigger='hover' desc={'Excluir'}>
            <Button
              color="danger"
              icon={deletingId === row.id ? undefined : "Delete"}
              size="sm"
              shadow="sm"
              onClick={() => handleDeleteDiscount(row.original)}
              disabled={deletingId === row.id}
            >
              {deletingId === row.id && <Spinner isSmall color="light" inButton="onlyIcon" />}
            </Button>
          </Popovers>
        </div>
      ),
    }
  ], [deletingId]);

  return (
    <PageWrapper title="Assinatura por loja">
      <SubHeader>
        <SubHeaderLeft>
          <Breadcrumb
            list={[
              { title: 'Assinatura', to: '/subscription/store' },
              { title: 'Loja', to: '/subscription/store' },
            ]}
          />
        </SubHeaderLeft>
        <SubHeaderRight>
          <div className="d-flex align-items-center gap-2">
            <Dropdown>
              <DropdownToggle>
                <div className="bg-white rounded-3 px-3 py-1 border d-flex align-items-center" style={{ height: '40px', cursor: 'pointer' }}>
                  <span className="fw-bold text-black">{selectedYear}</span>
                </div>
              </DropdownToggle>
              <DropdownMenu>
                {Array.from({ length: Math.max(0, currentYear - 2025 + 1) }, (_, i) => 2025 + i).map(year => (
                  <DropdownItem key={year} onClick={() => setSelectedYear(year)}>
                    {String(year)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
            <div className="d-flex">
              {months.map((month) => (
                <Button
                  key={month}
                  color={selectedMonth === month ? 'warning' : 'light'}
                  className={`rounded-3 mx-1 border-0 ${selectedMonth === month ? 'fw-bold' : 'text-muted'}`}
                  size="sm"
                  onClick={() => setSelectedMonth(month)}
                  style={{ minWidth: '40px' }}
                >
                  {month}
                </Button>
              ))}
            </div>
          </div>
        </SubHeaderRight>
      </SubHeader>
      <Page container='fluid'>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <Card className="mb-5">
              <CardBody>
                <h4 className='mb-3'>Assinatura por loja</h4>
                <div className="row d-flex align-items-end mb-0">
                  <SelectEstablishment class="col-md-3" />
                  <SelectStore class="col-md-3" />
                </div>
                <TableCustom
                  title=""
                  columns={columns}
                  rows={rows}
                  fileName={'Assinatura-por-loja'}
                />
              </CardBody>
            </Card>

            <Card className="mb-5">
              <CardBody>
                <h4 className='mb-3'>Locação por loja</h4>
                <div className="row d-flex align-items-end mb-0">
                  <SelectEstablishment class="col-md-3" />
                  <SelectStore class="col-md-3" />
                </div>
                <TableCustom
                  title=""
                  columns={columnsLocacao}
                  rows={rows}
                  fileName={'Locacao'}
                />
              </CardBody>
            </Card>
          </>
        )}
      </Page>
      <DiscountModal
        isOpen={isDiscountModalOpen}
        setIsOpen={setIsDiscountModalOpen}
        data={selectedCharge}
        onSuccess={handleRefresh}
      />
    </PageWrapper>
  );
};

export default StoreSubscription;
